import { computed, ref } from 'vue';
import Encoding from 'encoding-japanese';
import {
  CompanyDepartmentCreateRequestBody,
  CompanyDepartmentUpdateRequestBody,
} from '@/interfaces/api/general/company';
import store from '@/store';
import {
  DepartmentCsvUploadResult,
  DepartmentTreeNode,
} from '@/interfaces/model/general/department';
import {
  CompanyDepartmentDeletionImpact,
  CompanyDepartmentMoveRequestBody,
} from '@/interfaces/api/general/company';
import {
  dispatchCreateCompanyDepartment,
  dispatchDeleteCompanyDepartment,
  dispatchGetCompanyDepartmentDeletionImpact,
  dispatchGetCompanyDepartments,
  dispatchGetCompanyDepartmentsCsv,
  dispatchGetCompanyDepartmentTree,
  dispatchMoveCompanyDepartment,
  dispatchPostCompanyDepartmentsCsv,
  dispatchUpdateCompanyDepartment,
} from '@/store/main/actionsMain';
import {
  readAssertedCompanyId,
  readAssertedDepartments,
  readAssertedUserProfile,
  readUserLocale,
} from '@/store/main/getters';
import { pruneTreeToScope } from '@/utils/departmentTree';

export const useDepartmentComposable = () => {
  const isFetching = ref(false);
  const departmentTree = ref<DepartmentTreeNode[]>([]);
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);
  const isDownloadingCsv = ref(false);
  const isUploadingCsv = ref(false);

  const getCompanyDepartments = async (): Promise<void> => {
    isFetching.value = true;
    try {
      await dispatchGetCompanyDepartments(store, {
        companyId: readAssertedCompanyId(store),
      });
    } catch (e) {
      console.error(e);
    } finally {
      isFetching.value = false;
    }
  };

  /**
   * ツリーを取得する。
   *
   * 1 社あたりの部署数は CSV の上限に合わせて 1,000 件までを想定しているため、
   * サーバ側でページングせず全件を取得してフロントで組み立てる
   * (表示順は display_order で決まり、path は UUID 順なので SQL では作れない)。
   */
  const getCompanyDepartmentTree = async (): Promise<void> => {
    isFetching.value = true;
    try {
      const response = await dispatchGetCompanyDepartmentTree(store, {
        companyId: readAssertedCompanyId(store),
      });
      departmentTree.value = response?.data ?? [];
    } catch (e) {
      console.error(e);
    } finally {
      isFetching.value = false;
    }
  };

  const getDeletionImpact = async (
    departmentId: string
  ): Promise<CompanyDepartmentDeletionImpact | undefined> => {
    try {
      const response = await dispatchGetCompanyDepartmentDeletionImpact(store, {
        companyId: readAssertedCompanyId(store),
        departmentId,
      });
      return response?.data;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  const createCompanyDepartment = async (
    data: CompanyDepartmentCreateRequestBody
  ): Promise<boolean> => {
    isCreating.value = true;
    try {
      await dispatchCreateCompanyDepartment(store, {
        companyId: readAssertedCompanyId(store),
        data: { ...data, name: data.name.trim() },
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      isCreating.value = false;
    }
  };

  const updateCompanyDepartment = async (
    departmentId: string,
    data: CompanyDepartmentUpdateRequestBody
  ): Promise<boolean> => {
    isUpdating.value = true;
    try {
      await dispatchUpdateCompanyDepartment(store, {
        companyId: readAssertedCompanyId(store),
        departmentId: departmentId,
        data: { ...data, name: data.name.trim() },
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      isUpdating.value = false;
    }
  };

  /**
   * 親の付け替え。
   *
   * path / 子孫への波及 / 循環 / 10 階層上限は DB 側のトリガが担保するので、
   * ここは parent_id と位置を送るだけでよい。
   */
  const moveCompanyDepartment = async (
    departmentId: string,
    data: CompanyDepartmentMoveRequestBody
  ): Promise<boolean> => {
    isUpdating.value = true;
    try {
      await dispatchMoveCompanyDepartment(store, {
        companyId: readAssertedCompanyId(store),
        departmentId,
        data,
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      isUpdating.value = false;
    }
  };

  const deleteCompanyDepartment = async (
    departmentId: string
  ): Promise<boolean> => {
    isDeleting.value = true;
    try {
      await dispatchDeleteCompanyDepartment(store, {
        companyId: readAssertedCompanyId(store),
        departmentId: departmentId,
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      isDeleting.value = false;
    }
  };

  const companyDepartments = computed(() => readAssertedDepartments(store));

  const ownDepartments = computed(() => {
    const user = readAssertedUserProfile(store);
    const userDepartments = user.departments || [];
    const userDepartmentUuidList = userDepartments.map((d) => d.uuid);
    const companyDepartments = readAssertedDepartments(store);
    return companyDepartments.filter((d) =>
      userDepartmentUuidList.includes(d.uuid)
    );
  });

  // Own department(s) plus all descendants, matching the self+descendant
  // scope used for the actual SDS edit permission check. Use this (not
  // `ownDepartments`) for "which departments may I assign to an SDS"
  // dropdowns; keep `ownDepartments` for default-selection/display, which
  // should stay limited to actual membership.
  const editableDepartments = computed(() => {
    const user = readAssertedUserProfile(store);
    const scopedDepartmentIds = user.scoped_department_ids || [];
    const companyDepartments = readAssertedDepartments(store);
    return companyDepartments.filter((d) =>
      scopedDepartmentIds.includes(d.uuid)
    );
  });

  // Same self+descendant scope as `editableDepartments`, but as a tree for
  // the hierarchical checkbox pickers.
  const editableDepartmentTree = computed(() => {
    const user = readAssertedUserProfile(store);
    const scopedDepartmentIds = new Set(user.scoped_department_ids || []);
    return pruneTreeToScope(departmentTree.value, scopedDepartmentIds);
  });

  // Same self+descendant scope as `editableDepartmentTree`/`editableDepartments`,
  // for call sites that only have a single department id to check (e.g. a
  // per-row edit/delete button) rather than a whole tree to prune.
  const isInEditableScope = (departmentId: string): boolean => {
    const user = readAssertedUserProfile(store);
    const scopedDepartmentIds = user.scoped_department_ids || [];
    return scopedDepartmentIds.includes(departmentId);
  };

  const isOwnDepartment = (departmentId: string): boolean => {
    const user = readAssertedUserProfile(store);
    const userDepartments = user.departments || [];
    return userDepartments.some(
      (userDepartment) => userDepartment.uuid === departmentId
    );
  };

  const isOwnDepartmentOrVisibleByRole = (departmentId: string): boolean => {
    const currentUser = readAssertedUserProfile(store);

    if (!currentUser || !currentUser.departments) {
      return false;
    }

    return (
      currentUser.departments.some(
        (userDepartment) => userDepartment.uuid === departmentId
      ) ||
      currentUser.role.permissions.some(
        (permission) => permission.id === 'SDS_EDIT_OTHER_DEPARTMENT'
      )
    );
  };

  const visibleCompanyDepartmentsByOwnOrRole = computed(() => {
    return companyDepartments.value.filter((department) =>
      isOwnDepartmentOrVisibleByRole(department.uuid)
    );
  });

  const downloadDepartmentsCsv = async (): Promise<void> => {
    isDownloadingCsv.value = true;
    try {
      const companyId = readAssertedCompanyId(store);
      const locale = readUserLocale(store);
      const lang = locale === 'ja-JP' ? 'ja' : 'en';
      const response = await dispatchGetCompanyDepartmentsCsv(store, {
        companyId,
        lang,
      });

      if (response.data) {
        // response.data is a Blob when responseType is 'blob'
        // Convert Blob to text first
        const unicodeArray = Encoding.stringToCode(response.data);
        const sjisArray = Encoding.convert(unicodeArray, 'SJIS', 'UNICODE');
        const blob = new Blob([new Uint8Array(sjisArray)], {
          type: 'text/csv;charset=shift-jis;',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'departments.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      isDownloadingCsv.value = false;
    }
  };

  const uploadDepartmentsCsv = async (
    file: File
  ): Promise<DepartmentCsvUploadResult> => {
    isUploadingCsv.value = true;
    try {
      const companyId = readAssertedCompanyId(store);
      const locale = readUserLocale(store);
      const lang = locale === 'ja-JP' ? 'ja' : 'en';
      const response = await dispatchPostCompanyDepartmentsCsv(store, {
        companyId,
        file,
        lang,
      });
      if (response.data) {
        return response.data;
      }
      throw new Error('No data in response');
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      isUploadingCsv.value = false;
    }
  };

  return {
    isFetching,
    departmentTree,
    getCompanyDepartmentTree,
    getDeletionImpact,
    isCreating,
    isUpdating,
    isDeleting,
    isDownloadingCsv,
    isUploadingCsv,
    getCompanyDepartments,
    createCompanyDepartment,
    updateCompanyDepartment,
    moveCompanyDepartment,
    deleteCompanyDepartment,
    downloadDepartmentsCsv,
    uploadDepartmentsCsv,
    companyDepartments,
    ownDepartments,
    editableDepartments,
    editableDepartmentTree,
    isInEditableScope,
    isOwnDepartment,
    isOwnDepartmentOrVisibleByRole,
    visibleCompanyDepartmentsByOwnOrRole,
  };
};
